import React from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import RowLoader from 'components/Sceletons/RowLoader';
import SignatureRulesModal from './SigningComponents/SignatureRulesModal';
import SigningCard from './SigningCard/SigningCard';
import NotPartisipant from './SigningComponents/NotPartisipant';
import CheckboxWithRules from './SigningComponents/CheckboxWithRules';
import SigningCertificate from './SigningComponents/SigningCertificate';
import classes from './SingingTab.module.scss';
import SignedEarlier from './SigningComponents/SignedEarlier';
import { message, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { resetSigningState } from 'store/slices/signingSlice';
import { updateDocumentSign } from 'store/reducers/documentsSocketsCreator';
import api from 'api';
import signature from 'api/signature';

type SingingTabProps = {
  closeDetailsModal: () => void;
  usersWithPermissions: any[];
  loadingUsersPermissions: boolean;
};

const SingingTab = ({closeDetailsModal, usersWithPermissions, loadingUsersPermissions}: SingingTabProps) => {
  const [isRulesModalOpen, setIsRulesModalOpen] = React.useState<boolean>(false);
  const [isSectionAvailable, setIsSectionAvailable] = React.useState<boolean>(true);
  const [displayingCertificatSigning, setDisplayingCertificatSigning] = React.useState<boolean>(false);
  const [isDownloadingSignature, setIsDownloadingSignature] = React.useState(false);

  const { selectedDocument } = useAppSelector((state) => state.documents);
  const { isDocumentLoading } = useAppSelector((state) => state.viewer);
  const { userData } = useAppSelector((state) => state.userData);
  const { messageApi } = useAppSelector((state) => state.windowState);
  const { signing, isSigningLoading, needSigningFromUsers, signingInfo } = useAppSelector((state) => state.signing);

  const needSigningIdsParticipants = needSigningFromUsers.filter(user => !user.signing_status).map(user => user.user_id);
  const idsParticipants = needSigningFromUsers.map(user => user.user_id);  
  const { t } = useTranslation();
  const dispatch = useAppDispatch();  

  React.useEffect(() => {
    if (signing[0]) {
      setIsSectionAvailable(needSigningIdsParticipants.includes(userData?.id!) && signingInfo?.status !== 1);
      setDisplayingCertificatSigning(idsParticipants.includes(userData?.id!) || userData?.id === signingInfo?.initiator?.id);
    };

    return () => {
      setIsSectionAvailable(true);
    }
  }, [signing, usersWithPermissions]);

  React.useEffect(() => {
    setTimeout(() => !userData?.has_sign && setIsRulesModalOpen(true), 1000);
  }, [userData]);

  const openRulesModal = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    setIsRulesModalOpen(true);
  };

  const deleteSigning = async () => {
    try {
      await api.deleteSigning({document_id: selectedDocument?.id!});
      dispatch(resetSigningState());
      closeDetailsModal();
      messageApi?.success(t('Documents.details.signing.successDeleteSigning'));
      dispatch(updateDocumentSign({...selectedDocument!, signing_status: null, status: 0}));
    } catch (e) {
      messageApi?.error(t('Documents.details.signing.errorDeleteSigning'));
    }
  };

  const handleDownloadSignature = async () => {
    if (!selectedDocument?.id) return;
    
    setIsDownloadingSignature(true);
    try {
      const response = await signature.getLatestSignature(selectedDocument.id);
      const signatureData = response.data;
      
      const documentName = selectedDocument.name;
      const nameWithoutExtension = documentName.substring(0, documentName.lastIndexOf('.')) || documentName;
      const signatureFileName = `${nameWithoutExtension}.p7s`;
      
      const blob = new Blob([signatureData], { type: 'application/pkcs7-signature' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = signatureFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(t('Documents.details.qesSigning.downloadSignature'));
    } catch (e) {
      console.error('Error downloading signature:', e);
      message.error(t('Documents.details.qesSigning.downloadSignatureError'));
    } finally {
      setIsDownloadingSignature(false);
    }
  };

  const signedEarlier = selectedDocument?.is_signed && selectedDocument?.signing_status === null;

  const displaySignSidebar = React.useCallback(
    (signedEarlier: Boolean) => {
      switch (signedEarlier) {
        case true:
          return <SignedEarlier />;
        default:
          return isDocumentLoading || isSigningLoading ? (
            <RowLoader height={400} width={400} padding={'0 0 0 0'} />
          ) : (
            <SigningCard
              closeDetailsModal={closeDetailsModal}
              deleteSigning={deleteSigning}
              usersWithPermissions={usersWithPermissions}
              loadingUsersPermissions={loadingUsersPermissions}
            />
          );
      }
    },
    [selectedDocument, isDocumentLoading, isSigningLoading, usersWithPermissions]
  );

  return (
    <div>
      {isSectionAvailable
        ? (
          <div className={classes.container}>
            <div className={classes.sidebar}>
              
              { displaySignSidebar(Boolean(signedEarlier)) }

              <CheckboxWithRules
                openRulesModal={openRulesModal}
                hasSign={userData?.has_sign}
              />
            </div>
          </div>
        )
        : (
          <div className={classes.container}>
            {displayingCertificatSigning ? (
              <>
                <SigningCertificate
                  deleteSigning={deleteSigning}
                  usersWithPermissions={usersWithPermissions}
                  loadingUsersPermissions={loadingUsersPermissions}
                />
                {signingInfo?.signature_type === 'qes' && (
                  <div className={classes.downloadSignatureWrapper}>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleDownloadSignature}
                      loading={isDownloadingSignature}
                      disabled={isDownloadingSignature}
                      block
                    >
                      {t('Documents.details.qesSigning.downloadSignature')}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <NotPartisipant />
            )}
          </div>
        )
      }

      {isRulesModalOpen && (
        <React.Suspense fallback={<div />}>
          <SignatureRulesModal
            isRulesModalOpen={isRulesModalOpen}
            setIsRulesModalOpen={setIsRulesModalOpen}
            isAlreadyAccept={Boolean(userData?.has_sign)}
          />
        </React.Suspense>
      )}
    </div>
  );
}

export default SingingTab;
